// Оформить возврат остатка. Сценарий 51.
//
// Подпись «возврат уменьшает остаток, но не оборот выдач» стоит в панели не для красоты:
// финотдел путает возврат с отчётом. Отчёт — это заявка с чеком, она уменьшает «не отчитано».
// Возврат — это деньги обратно в кассу, он уменьшает остаток и оборот не трогает.
//
// Возврат больше остатка НЕ отвергается: остаток бывает отрицательным, значит компания должна
// человеку, и у заказчика такое есть. Сервер вернёт предупреждение, а не отказ.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { CASH_METHODS, ошибкаДаты, type CashMethod } from '@/shared/api/endpoints/accountable';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { METHOD_TITLES } from '../dictionaries';
import { usePerson, useRegisterReturn } from '../queries';

const RETURN_PERMISSION = 'core.accountable.return';

export function ReturnPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, RETURN_PERMISSION);
  const лицо = usePerson(id);
  const операция = useRegisterReturn();

  const [сумма, setСумма] = useState('');
  const [способ, setСпособ] = useState<CashMethod>('cash');
  // Пусто значит «сейчас»: см. тот же довод в панели выдачи.
  const [дата, setДата] = useState('');
  const [ошибкаСуммы, setОшибкаСуммы] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [ключ, setКлюч] = useState(newIdempotencyKey);

  const итог = операция.data;
  // Пока остаток не приехал, он именно НЕИЗВЕСТЕН, а не равен нулю. Прежний `?? 0` рисовал в
  // шапке панели «0,00» на всё время запроса: человек видел конкретное неверное число и по нему
  // решал, сколько принимать обратно (правило 7 и правило 11 в ПРАВИЛА.md).
  const остаток = лицо.data?.balance.unconfirmed_balance ?? null;
  // Отказ по дате живёт ПОД полем даты, а общей плашкой внизу не дублируется: одна и та же
  // фраза в двух местах читается как две разные ошибки, и человек ищет вторую.
  const ошибкаДатыПоля = ошибкаДаты(операция.error);

  function отправить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗапрет('Проводить возврат может финансовый отдел. Попросите его оформить.');
      return;
    }
    if (операция.isPending) {
      setЗапрет('Возврат уже проводится, ждём ответ сервера.');
      return;
    }
    const разбор = parseRublesToKopecks(сумма);
    if (!разбор.ok) {
      setОшибкаСуммы(moneyErrorText(разбор.reason, 'сумма возврата'));
      return;
    }
    setОшибкаСуммы(undefined);
    setЗапрет(null);
    операция.mutate(
      {
        body: {
          accountable_person_id: id,
          amount: разбор.value,
          method: способ,
          returned_at: дата === '' ? null : дата,
        },
        idempotencyKey: ключ,
      },
      {
        onSuccess: () => {
          setКлюч(newIdempotencyKey());
          setСумма('');
          // Как и у выдачи: панель остаётся открытой, и прошлая дата легла бы в следующую
          // операцию, которую уже ничем не исправить.
          setДата('');
        },
      },
    );
  }

  return (
    <form className='flex flex-col gap-4' onSubmit={отправить}>
      <p className='text-paragraph-sm text-text-sub-600'>
        От кого: {лицо.data?.employee_name ?? 'загружаем…'} · сейчас на нём{' '}
        {остаток === null ? 'загружаем…' : formatKopecks(остаток)}
      </p>

      <Alert.Root variant='lighter' status='information' size='small'>
        <Alert.Icon />
        Возврат — это деньги обратно в кассу. Он уменьшает остаток, но НЕ уменьшает оборот выдач: за
        месяц человеку выдали столько, сколько выдали, и это факт истории. Отчитаться за трату —
        другое действие, там нужна заявка с чеком.
      </Alert.Root>

      <TextField
        id='return-amount'
        label='Сумма'
        required
        value={сумма}
        onChange={setСумма}
        // Подсказка — образец записи, а не остаток человека. Раньше здесь стоял сам остаток,
        // поделённый на сто и округлённый: 1 234 567,89 показывалось как «1234568», то есть в
        // чужом формате и на одиннадцать копеек больше правды. Настоящий остаток стоит строкой
        // выше, с подписью, и второе его написание рядом только сбивало.
        placeholder='30 000'
        error={ошибкаСуммы}
      />

      <SelectField
        id='return-method'
        label='Как вернул'
        required
        value={способ}
        placeholder='наличными или переводом'
        options={CASH_METHODS.map((код) => ({ value: код, label: METHOD_TITLES[код] }))}
        onChange={(value) => setСпособ(value as CashMethod)}
      />

      <DateField
        id='return-date'
        label='Когда вернули'
        value={дата}
        onChange={setДата}
        error={ошибкаДатыПоля}
        hint='Пусто — сейчас. Дата нужна, когда заносите возврат, который уже случился: от неё считается давность остатка.'
      />

      {запрет !== null && (
        <Alert.Root variant='lighter' status='warning' size='small'>
          <Alert.Icon />
          {запрет}
        </Alert.Root>
      )}
      {операция.error !== null && ошибкаДатыПоля === undefined && (
        <ErrorAlert error={операция.error as never} />
      )}

      {итог !== undefined && (
        <Alert.Root
          variant='lighter'
          status={итог.warning !== null ? 'warning' : 'success'}
          size='small'
        >
          <Alert.Icon />
          Возвращено {formatKopecks(итог.amount)}.{' '}
          {итог.balance_after === null ? (
            <>Новый остаток сервер не прислал — посмотрите его в карточке человека.</>
          ) : (
            <>Остаток стал {formatKopecks(итог.balance_after)}.</>
          )}
          {итог.warning !== null && <> {итог.warning}</>}
        </Alert.Root>
      )}

      <div className='flex gap-2'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {операция.isPending ? 'Проводим…' : 'Провести возврат'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          {итог !== undefined ? 'Готово' : 'Отмена'}
        </Button.Root>
      </div>
    </form>
  );
}
